import type { Place, PlaceStatus } from "@couples/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

import PlaceCard from "../components/PlaceCard";
import PlaceSheet from "../components/PlaceSheet";
import QueryState from "../components/QueryState";
import ScreenHeader from "../components/ScreenHeader";
import { useConfirm } from "../lib/confirm";
import { todayDateKey } from "../lib/dates";
import { createPlace, deletePlace, fetchPlaces, updatePlace, type PlaceInput } from "../lib/places";
import { queryKeys } from "../lib/queryKeys";
import { useToast } from "../lib/toast";

function chipClass(active: boolean): string {
  return active
    ? "bg-[var(--color-teal)] text-white"
    : "border border-[var(--color-line)] text-[var(--color-sub)]";
}

/** 共有リストの「行ってみたい場所」「行った場所」タブ */
export default function Places() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const [filter, setFilter] = useState<PlaceStatus>("want");
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState<Place | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const places = useQuery({ queryKey: queryKeys.places, queryFn: fetchPlaces });

  const wantList = useMemo(
    () => (places.data ?? []).filter((place) => place.status === "want"),
    [places.data],
  );
  // 行った場所は「最近行った順」。visited_on は YYYY-MM-DD なので文字列比較で並ぶ
  const visitedList = useMemo(
    () =>
      (places.data ?? [])
        .filter((place) => place.status === "visited")
        .sort((a, b) => (b.visitedOn ?? "").localeCompare(a.visitedOn ?? "")),
    [places.data],
  );
  const visibleList = filter === "want" ? wantList : visitedList;

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: queryKeys.places });
  }

  async function handleSave(input: PlaceInput) {
    setSaving(true);
    try {
      if (editing !== null) {
        await updatePlace(editing.id, input);
        toast("場所を直しました。");
      } else {
        await createPlace(input);
        toast("場所を追加しました。");
      }
      await refresh();
      setComposing(false);
      setEditing(null);
    } catch (e) {
      console.error("場所の保存に失敗", e);
      toast("場所の保存に失敗しました。", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(place: Place) {
    const ok = await confirm({
      message: `「${place.name}」を削除します。`,
      confirmLabel: "削除",
      tone: "danger",
    });
    if (!ok) return;
    setBusyId(place.id);
    try {
      await deletePlace(place.id);
      await refresh();
      toast(`「${place.name}」を削除しました。`);
    } catch (e) {
      console.error("場所の削除に失敗", e);
      toast("場所の削除に失敗しました。", "error");
    } finally {
      setBusyId(null);
    }
  }

  async function moveTo(place: Place, status: PlaceStatus, visitedOn: string | null) {
    setBusyId(place.id);
    try {
      await updatePlace(place.id, { name: place.name, memo: place.memo, status, visitedOn });
      await refresh();
      toast(
        status === "visited"
          ? `「${place.name}」を行った場所に移しました。`
          : `「${place.name}」を行きたい場所に戻しました。`,
      );
    } catch (e) {
      console.error("場所の更新に失敗", e);
      toast("場所の更新に失敗しました。", "error");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <ScreenHeader
        navKey="lists"
        actions={[
          {
            label: "追加",
            icon: Plus,
            onSelect: () => {
              setComposing(true);
            },
            primary: true,
          },
        ]}
      />

      <div className="relative flex-1">
        <div className="flex flex-col gap-3 px-4 pt-3.5 pb-24">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setFilter("want");
              }}
              aria-pressed={filter === "want"}
              className={`flex-1 rounded-full px-3 py-1.5 text-xs font-bold ${chipClass(filter === "want")}`}
            >
              行きたい{wantList.length > 0 ? `(${String(wantList.length)})` : ""}
            </button>
            <button
              type="button"
              onClick={() => {
                setFilter("visited");
              }}
              aria-pressed={filter === "visited"}
              className={`flex-1 rounded-full px-3 py-1.5 text-xs font-bold ${chipClass(filter === "visited")}`}
            >
              行った{visitedList.length > 0 ? `(${String(visitedList.length)})` : ""}
            </button>
          </div>

          <QueryState
            isPending={places.isPending}
            isError={places.isError}
            errorMessage="場所の取得に失敗しました。"
            isEmpty={places.isSuccess && visibleList.length === 0}
            emptyMessage={
              filter === "want"
                ? "行ってみたい場所はまだありません。"
                : "行った場所はまだありません。"
            }
            emptyClassName="py-10 text-center text-sm"
          />

          {visibleList.map((place) => (
            <PlaceCard
              key={place.id}
              place={place}
              busy={busyId === place.id}
              onEdit={() => {
                setEditing(place);
              }}
              onDelete={() => void handleDelete(place)}
              onMarkVisited={() => void moveTo(place, "visited", todayDateKey())}
              onMarkWant={() => void moveTo(place, "want", null)}
            />
          ))}
        </div>

        {/* 一覧が伸びるほど下に沈む absolute ではなく、ビューポート基準の fixed で
            常にスクロール位置に関わらず親指の届く位置に留める。PC は上の主操作ボタンが受け持つ */}
        <div className="pointer-events-none fixed inset-x-0 z-40 mx-auto flex max-w-[var(--content-w)] justify-end px-4 bottom-[calc(4rem+env(safe-area-inset-bottom))] xl:hidden">
          <button
            type="button"
            onClick={() => {
              setComposing(true);
            }}
            aria-label="場所を追加"
            className="pointer-events-auto flex size-[46px] items-center justify-center rounded-full bg-[var(--color-teal)] shadow-lg"
          >
            <Plus className="size-5 text-white" aria-hidden />
          </button>
        </div>
      </div>

      {composing && (
        <PlaceSheet
          busy={saving}
          onSave={(input) => void handleSave(input)}
          onClose={() => {
            setComposing(false);
          }}
        />
      )}

      {editing !== null && (
        <PlaceSheet
          editingPlace={editing}
          busy={saving}
          onSave={(input) => void handleSave(input)}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </>
  );
}
